import { useCallback, useEffect, useState } from 'react';
import { getUserInfo } from '../../Access/services/authService';
import {
  NL_SEARCH_IDLE_MAX_ITEMS,
  NL_SEARCH_INTENTS,
  NL_SEARCH_RECENT_SCOPE_MAP,
  NL_SEARCH_SUGGESTED_QUERIES,
} from '../data/nlSearchConstants';
import {
  buildNlSearchIdleLists,
  clearNlSearchRecentQueries,
  forgetNlSearchQuery,
  readNlSearchRecentQueries,
  rememberNlSearchQuery,
} from '../utils/nlSearchRecentQueries';

function resolveUserKey() {
  const info = getUserInfo();
  return info?.sub || info?.email || info?.username || null;
}

/**
 * Recientes NL en localStorage (por `sub` y por vista) + relleno con sugeridas.
 * @param {{
 *   enabled?: boolean,
 *   suggestedQueries?: string[],
 *   scope?: string,
 * }} [options]
 */
export function useNlSearchIdleSuggestions({
  enabled = true,
  suggestedQueries = NL_SEARCH_SUGGESTED_QUERIES,
  scope = NL_SEARCH_RECENT_SCOPE_MAP,
} = {}) {
  const [recentEntries, setRecentEntries] = useState([]);

  const refresh = useCallback(() => {
    if (!enabled) {
      setRecentEntries([]);
      return;
    }
    setRecentEntries(readNlSearchRecentQueries(resolveUserKey(), scope));
  }, [enabled, scope]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const rememberQuery = useCallback((query, intent) => {
    if (!enabled) return;
    if (intent === NL_SEARCH_INTENTS.unknown) return;
    const next = rememberNlSearchQuery(resolveUserKey(), query, scope);
    setRecentEntries(next);
  }, [enabled, scope]);

  const forgetQuery = useCallback((query) => {
    if (!enabled) return;
    const next = forgetNlSearchQuery(resolveUserKey(), query, scope);
    setRecentEntries(next);
  }, [enabled, scope]);

  const clearRecents = useCallback(() => {
    clearNlSearchRecentQueries(resolveUserKey(), scope);
    setRecentEntries([]);
  }, [scope]);

  const idleLists = enabled
    ? buildNlSearchIdleLists({
      recentQueries: recentEntries.map((item) => item.query),
      suggestedQueries,
      maxItems: NL_SEARCH_IDLE_MAX_ITEMS,
    })
    : { recentQueries: [], suggestedQueries: [] };

  return {
    ...idleLists,
    rememberQuery,
    forgetQuery,
    clearRecents,
    refresh,
  };
}
